import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, openDocument } from '../../api/client';
import { Button, Card, EmptyState, Field, Spinner, formatDateTime } from '../../components/ui';
import { useToast } from '../../components/Toast';

const DOC_TYPE_VALUES = ['identification', 'certificate', 'first_aid', 'police_check', 'qualification', 'reference', 'other'];

function fileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function NannyDocuments() {
  const { t } = useTranslation();
  const toast = useToast();
  const [documents, setDocuments] = useState(null);
  const [docType, setDocType] = useState('identification');
  const [label, setLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api
      .get('/api/nanny/documents')
      .then(({ documents: docs }) => setDocuments(docs))
      .catch((err) => toast.error(err.message));
  }, [toast]);

  async function onUpload(event) {
    event.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error(t('nannyDocuments.chooseFile'));
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('docType', docType);
      formData.append('label', label);
      const { document: uploaded } = await api.upload('/api/nanny/documents', formData);
      setDocuments((prev) => [uploaded, ...(prev || [])]);
      setLabel('');
      fileRef.current.value = '';
      toast.success(t('nannyDocuments.documentUploaded'));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  }

  async function remove(id) {
    if (!window.confirm(t('nannyDocuments.confirmRemove'))) return;
    try {
      await api.del(`/api/nanny/documents/${id}`);
      setDocuments((prev) => prev.filter((d) => d.id !== id));
      toast.success(t('nannyDocuments.documentRemoved'));
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function view(id) {
    try {
      await openDocument(id);
    } catch (err) {
      toast.error(err.message);
    }
  }

  if (!documents) return <Spinner />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('nannyDocuments.pageTitle')}</h1>
          <p className="muted">{t('nannyDocuments.subtitle')}</p>
        </div>
      </div>


      <Card title={t('nannyDocuments.uploadTitle')}>
        <form onSubmit={onUpload}>
          <div className="grid-2">
            <Field label={t('nannyDocuments.docType')} required>
              <select value={docType} onChange={(e) => setDocType(e.target.value)}>
                {DOC_TYPE_VALUES.map((value) => (
                  <option key={value} value={value}>{t(`nannyDocuments.docTypes.${value}`)}</option>
                ))}
              </select>
            </Field>
            <Field label={t('nannyDocuments.description')}>
              <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t('nannyDocuments.descriptionPlaceholder')} />
            </Field>
          </div>
          <Field label={t('nannyDocuments.file')} required hint={t('nannyDocuments.fileHint')}>
            <input type="file" ref={fileRef} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.heic,.webp" required />
          </Field>
          <Button type="submit" loading={uploading}>{t('nannyDocuments.uploadDocument')}</Button>
        </form>
      </Card>

      <Card title={t('nannyDocuments.uploadedDocuments', { count: documents.length })}>
        {documents.length ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('nannyDocuments.document')}</th>
                  <th>{t('nannyDocuments.type')}</th>
                  <th>{t('nannyDocuments.size')}</th>
                  <th>{t('nannyDocuments.uploaded')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc.id}>
                    <td>
                      <strong>{doc.originalName}</strong>
                      {doc.label && <small className="muted"> · {doc.label}</small>}
                    </td>
                    <td>{t(`nannyDocuments.docTypes.${doc.docType}`, { defaultValue: doc.docType })}</td>
                    <td>{fileSize(doc.sizeBytes)}</td>
                    <td>{formatDateTime(doc.uploadedAt)}</td>
                    <td className="row-actions">
                      <button type="button" className="link" onClick={() => view(doc.id)}>{t('nannyDocuments.view')}</button>
                      <button type="button" className="link danger" onClick={() => remove(doc.id)}>{t('nannyDocuments.remove')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title={t('nannyDocuments.noDocumentsYet')}>{t('nannyDocuments.noDocumentsBody')}</EmptyState>
        )}
      </Card>
    </>
  );
}
